import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

import { AddSceneTool } from 'js/src/agent/tools/AddSceneTool';
import { AddVoiceoverSceneTool } from 'js/src/agent/tools/AddVoiceoverSceneTool';
import { MOTION_GRAPHICS_SCENE_DESIGN_ID } from 'js/src/agent/tools/motion-graphics/motionGraphicsSceneDesign';
import { DesignMotionGraphicsSceneTool } from 'js/src/agent/tools/motion-graphics/DesignMotionGraphicsSceneTool';
import { SetMediaTool } from 'js/src/agent/tools/SetMediaTool';
import { SetTextTool } from 'js/src/agent/tools/SetTextTool';
import { ASSET_TYPES } from 'js/src/agent/video-state/constants';

/**
 * `instructions` is a getter for the same reason as change-template: the tool classes it
 * interpolates transitively import the skills registry, so reading `Tool.toolName` at module scope
 * would run while a tool module is still mid-evaluation. See changeTemplate.ts for the full note.
 */
export const motionGraphicsScenesBasicsSkill: Skill = {
    name: SKILL_NAMES.motionGraphicsScenesBasics,
    description:
        "Bespoke animated graphics for content a stock scene design can't express — animated " +
        'numbers and data callouts, diagrams and step-by-step explainers, product-UI mockups, ' +
        'stylized typographic moments. They can stand alone as a scene or cover a span of ' +
        'narration. Load BEFORE adding or changing one.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Motion Graphics Scenes Basics

A motion graphics scene is a bespoke animated graphic — an HTML composition built for
this video — rather than a stock scene design. A specialist motion-design subagent
authors it from a brief you write.

## When to reach for one
It is motion and bespoke layout that earn a motion graphics scene. A static stat on a
background is not the case for one — a stock design does that well.

Prefer a stock scene design when footage, photography or a talking head should carry
the scene, or when a standard text-and-media layout serves. Motion graphics scenes
are slower and costlier to build.

## Building one
Two steps, deliberately separate:

1. **\`${AddSceneTool.toolName}\`** with a \`scenes\` entry whose
   \`scene_design_id\` is \`"${MOTION_GRAPHICS_SCENE_DESIGN_ID}"\` — the reserved id that adds a
   scene and clears it, instead of using one of the template's designs.
2. **\`${DesignMotionGraphicsSceneTool.toolName}\`** hands a brief to the design subagent,
   which builds the graphic. The same tool changes an existing graphic later — pass the
   same \`scene_id\` with an instruction describing what to change.

**Adding several in a row:** make one \`${AddSceneTool.toolName}\` call listing every scene
in \`scenes\`, in the order you want them — they are inserted in series, so there is no
need to chain a call per scene. Then issue every
\`${DesignMotionGraphicsSceneTool.toolName}\` together in a single turn — those are the
slow half and they build in parallel.

An added scene shows nothing until it is designed. Never leave one undesigned; the
video state reports it as \`status: empty (awaiting generation)\`.

## Over a span of narration
To change what a moment in the middle of a voiceover shows, use
**\`${AddVoiceoverSceneTool.toolName}\`** with the same reserved id, naming the transcript
words the graphic should cover. Same two steps from there.

The graphic inherits the narration it covers, so the brief can lean on what is being
said. **The scene underneath stays as the base — do not delete it.**

## Writing the brief
Write intent, not implementation: what the scene must communicate, the exact copy and
numbers to show, and the tone. The subagent owns layout, motion and palette, and picks
up the template's colors and fonts automatically — don't specify them.

**One idea per graphic.** A brief carrying two — a stat *and* a three-step process —
gets a cluttered scene, not a richer one. Give each its own motion graphics scene, or
cut to the one that matters. Keep the copy to what must appear on screen: over
narration, the few words that land, not a restatement of what is being said.

For a change, refer to the parts listed for that scene in the video state so the
instruction is unambiguous ("make the 38% headline gold", not "make it gold").

## Checking the result
Trust the subagent's report by default — it reviews its own work across the scene's
timeline, which no single still of an animated graphic can show you. What is worth
checking yourself is the copy: compare the parts listed for the scene in the video state
against the numbers and wording your brief asked for.

Render the scene only as a fallback — the report flags something unresolved, the brief was
intricate, or you have a specific doubt. Either way, fixes go back through
\`${DesignMotionGraphicsSceneTool.toolName}\`.

## What you cannot do to one
Its content is a single ${ASSET_TYPES.MOTION_GRAPHIC} composition owned by the design
subagent — the one exception to "assets cannot be added or removed".
\`${SetTextTool.toolName}\`, \`${SetMediaTool.toolName}\` and the other asset tools do not
apply to it; every change goes through \`${DesignMotionGraphicsSceneTool.toolName}\`.`;
    },
};
